import React, { useState, useEffect } from 'react';
import { Gamepad, Undo, RotateCcw } from 'lucide-react';

const BOARD_SIZE = 4;
const SUBGRID_SIZE = 2;
const DIGITS = [1, 2, 3, 4];

const SudokuGrid = ({ setIsSudokuSolved }) => {
  const [showIntro, setShowIntro] = useState(true);

  const createEmptyGrid = () => Array(BOARD_SIZE).fill().map(() => Array(BOARD_SIZE).fill(0));

  const shuffleArray = (array) => {
    const copy = [...array];
    for (let i = copy.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy;
  };

  const isValid = (grid, row, col, num) => {
    for (let x = 0; x < BOARD_SIZE; x++) {
      if (grid[row][x] === num) return false;
    }

    for (let x = 0; x < BOARD_SIZE; x++) {
      if (grid[x][col] === num) return false;
    }

    const boxRow = Math.floor(row / SUBGRID_SIZE) * SUBGRID_SIZE;
    const boxCol = Math.floor(col / SUBGRID_SIZE) * SUBGRID_SIZE;
    for (let i = 0; i < SUBGRID_SIZE; i++) {
      for (let j = 0; j < SUBGRID_SIZE; j++) {
        if (grid[boxRow + i][boxCol + j] === num) return false;
      }
    }

    return true;
  };

  const findEmpty = (grid) => {
    for (let i = 0; i < BOARD_SIZE; i++) {
      for (let j = 0; j < BOARD_SIZE; j++) {
        if (grid[i][j] === 0) return [i, j];
      }
    }
    return null;
  };

  const solveSudoku = (grid) => {
    const empty = findEmpty(grid);
    if (!empty) return true;

    const [row, col] = empty;
    const nums = shuffleArray(DIGITS);

    for (const num of nums) {
      if (isValid(grid, row, col, num)) {
        grid[row][col] = num;
        if (solveSudoku(grid)) return true;
        grid[row][col] = 0;
      }
    }

    return false;
  };

  const generatePuzzle = () => {
    const grid = createEmptyGrid();
    solveSudoku(grid);

    const positions = shuffleArray(
      Array.from({ length: BOARD_SIZE * BOARD_SIZE }, (_, i) => [Math.floor(i / BOARD_SIZE), i % BOARD_SIZE])
    );

    const cellsToRemove = 8;
    for (let i = 0; i < cellsToRemove; i++) {
      const [row, col] = positions[i];
      grid[row][col] = 0;
    }

    return { puzzle: grid };
  };

  const [initialState] = useState(() => generatePuzzle());
  const [sudokuGrid, setSudokuGrid] = useState(initialState.puzzle);
  const [gridHistory, setGridHistory] = useState([initialState.puzzle]);
  const [selectedCell, setSelectedCell] = useState(null);
  const [errors, setErrors] = useState([]);

  useEffect(() => {
    if (!showIntro) return;

    const introTimer = setTimeout(() => {
      setShowIntro(false);
    }, 3000);

    return () => clearTimeout(introTimer);
  }, [showIntro]);

  const checkValidityAt = (grid, row, col, num) => {
    for (let x = 0; x < BOARD_SIZE; x++) {
      if (x !== col && grid[row][x] === num) {
        return { isValid: false, message: `${num} already exists in this row` };
      }
    }

    for (let x = 0; x < BOARD_SIZE; x++) {
      if (x !== row && grid[x][col] === num) {
        return { isValid: false, message: `${num} already exists in this column` };
      }
    }

    const boxRow = Math.floor(row / SUBGRID_SIZE) * SUBGRID_SIZE;
    const boxCol = Math.floor(col / SUBGRID_SIZE) * SUBGRID_SIZE;
    for (let i = 0; i < SUBGRID_SIZE; i++) {
      for (let j = 0; j < SUBGRID_SIZE; j++) {
        if (boxRow + i !== row || boxCol + j !== col) {
          if (grid[boxRow + i][boxCol + j] === num) {
            return { isValid: false, message: `${num} already exists in this 2x2 box` };
          }
        }
      }
    }

    return { isValid: true, message: '' };
  };

  const handleNumberInput = (num) => {
    if (!selectedCell) return;

    if (initialState.puzzle[selectedCell.row][selectedCell.col] !== 0) {
      return;
    }

    const newGrid = sudokuGrid.map((row) => [...row]);

    if (num !== 0) {
      const validity = checkValidityAt(newGrid, selectedCell.row, selectedCell.col, num);
      if (!validity.isValid) {
        setErrors([{ row: selectedCell.row, col: selectedCell.col, message: validity.message }]);
        return;
      }
    }

    newGrid[selectedCell.row][selectedCell.col] = num;
    setSudokuGrid(newGrid);
    setGridHistory((prev) => [...prev, newGrid]);
    setErrors([]);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedCell) return;

      if (e.key >= '1' && e.key <= String(BOARD_SIZE)) {
        handleNumberInput(parseInt(e.key, 10));
      } else if (e.key === 'Backspace' || e.key === 'Delete') {
        handleNumberInput(0);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedCell, sudokuGrid]);

  const handleCellClick = (rowIndex, colIndex) => {
    if (errors.length > 0) {
      const newGrid = sudokuGrid.map((row) => [...row]);
      errors.forEach((error) => {
        newGrid[error.row][error.col] = 0;
      });
      setSudokuGrid(newGrid);
      setErrors([]);
    }
    setSelectedCell({ row: rowIndex, col: colIndex });
  };

  const handleUndo = () => {
    if (gridHistory.length <= 1) return;

    const newHistory = gridHistory.slice(0, -1);
    setSudokuGrid(newHistory[newHistory.length - 1]);
    setGridHistory(newHistory);
    setErrors([]);
  };

  const handleReset = () => {
    setSudokuGrid(initialState.puzzle);
    setGridHistory([initialState.puzzle]);
    setErrors([]);
  };

  const getBorderStyle = (rowIndex, colIndex) => {
    let borderClasses = 'border border-pink-400';
    if (rowIndex % SUBGRID_SIZE === 0 && rowIndex !== 0) borderClasses += ' border-t-4 border-t-pink-600';
    if (colIndex % SUBGRID_SIZE === 0 && colIndex !== 0) borderClasses += ' border-l-4 border-l-pink-600';
    return borderClasses;
  };

  const isValidCompletedSudoku = (grid) => {
    const checkSet = (arr) => {
      const s = new Set(arr);
      return s.size === BOARD_SIZE && [...s].every((n) => n >= 1 && n <= BOARD_SIZE);
    };

    for (let i = 0; i < BOARD_SIZE; i++) {
      if (!checkSet(grid[i])) return false;
    }

    for (let j = 0; j < BOARD_SIZE; j++) {
      const col = grid.map((row) => row[j]);
      if (!checkSet(col)) return false;
    }

    for (let br = 0; br < BOARD_SIZE / SUBGRID_SIZE; br++) {
      for (let bc = 0; bc < BOARD_SIZE / SUBGRID_SIZE; bc++) {
        const box = [];
        for (let i = 0; i < SUBGRID_SIZE; i++) {
          for (let j = 0; j < SUBGRID_SIZE; j++) {
            box.push(grid[br * SUBGRID_SIZE + i][bc * SUBGRID_SIZE + j]);
          }
        }
        if (!checkSet(box)) return false;
      }
    }

    return true;
  };

  const handleSubmit = () => {
    const isComplete = sudokuGrid.every((row) => row.every((cell) => cell !== 0));

    if (!isComplete) {
      alert('Please fill in all cells before submitting!');
      return;
    }

    if (isValidCompletedSudoku(sudokuGrid)) {
      setIsSudokuSolved(true);
    } else {
      alert('This solution violates Sudoku rules.');
    }
  };

  if (showIntro) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-pink-50">
        <div className="text-2xl text-black-600 animate-pulse font-bold">
          i have to ask you something. but first i need some help...
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center h-screen bg-pink-50">
      <Gamepad color="pink" size={64} className="mb-4" />
      <h1 className="text-2xl mb-4">Solve the Sudoku to reveal something :o</h1>
      <p className="text-sm mb-4">Use keyboard numbers (1-4) to fill cells. Backspace to clear.</p>
      <div className="grid grid-cols-4 gap-0 bg-pink-200 p-4 rounded-xl">
        {sudokuGrid.map((row, rowIndex) =>
          row.map((cell, colIndex) => {
            const hasError = errors.some((e) => e.row === rowIndex && e.col === colIndex);
            return (
              <button
                key={`${rowIndex}-${colIndex}`}
                onClick={() => handleCellClick(rowIndex, colIndex)}
                className={`w-16 h-16 text-center text-2xl ${getBorderStyle(rowIndex, colIndex)}
                  ${cell === 0 ? 'bg-white' : 'bg-pink-300 font-bold'}
                  ${selectedCell?.row === rowIndex && selectedCell?.col === colIndex ? 'border-red-500 border-2' : ''}
                  ${hasError ? 'bg-red-200' : ''}
                  ${initialState.puzzle[rowIndex][colIndex] !== 0 ? 'text-gray-600' : 'text-black'}`}
              >
                {cell || ''}
              </button>
            );
          })
        )}
      </div>
      {errors.length > 0 && <div className="mt-2 text-red-500">{errors[0].message}</div>}
      <div className="mt-4 flex space-x-2">
        <button
          onClick={handleUndo}
          className="bg-yellow-500 text-white p-2 rounded hover:bg-yellow-600 flex items-center"
        >
          <Undo size={20} className="mr-2" /> Undo
        </button>
        <button
          onClick={handleReset}
          className="bg-orange-500 text-white p-2 rounded hover:bg-orange-600 flex items-center"
        >
          <RotateCcw size={20} className="mr-2" /> Reset
        </button>
        <button
          onClick={handleSubmit}
          className="bg-green-500 text-white p-2 rounded hover:bg-green-800"
        >
          Submit
        </button>
      </div>
    </div>
  );
};

export default SudokuGrid;
